import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setLoading(true);
    try {
      const data = await login(email.trim(), password);
      if (data.mfa_required) { setMfaToken(data.mfa_token); setLoading(false); return; }
      nav("/oggi");
    } catch (e) { setErr(apiError(e)); setLoading(false); }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    setErr(""); setLoading(true);
    try {
      await verify2fa(mfaToken, code.trim(), remember);
      nav("/oggi");
    } catch (e) {
      if (e.response?.status === 440) {
        // Login challenge expired: send the user back to the password step.
        setMfaToken(null); setCode(""); setErr("Sessione scaduta, rifai il login.");
      } else {
        setErr(apiError(e));
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white text-[#0F0F12]">
      {/* Hero */}
      <div className="hidden lg:flex flex-col justify-center w-[52%] px-16 xl:px-24 relative border-r border-slate-100">
        <div className="max-w-md">
          <img src="/logo-immobilart.webp" alt="IMMOBILART" data-testid="login-logo" className="w-[200px] h-auto mb-14" />
          <div className="flex items-center gap-2 mb-5">
            <span className="w-6 h-px bg-[#FC82ED]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#FC82ED]">Sistema Operativo Immobiliare</span>
          </div>
          <h1 className="font-display text-[2.75rem] xl:text-5xl font-extrabold leading-[1.04] tracking-tight text-[#0F0F12]">
            Il metodo Immobilart<br/><span className="text-[#FC82ED]">a portata di mano.</span>
          </h1>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center lg:justify-start px-6 lg:pl-20 xl:pl-28">
        {!mfaToken ? (
          <form onSubmit={submit} className="w-full max-w-[340px]">
            <div className="lg:hidden mb-8"><Logo width={200} /></div>
            <h2 className="font-display text-[1.7rem] font-bold tracking-tight mb-1.5">Accedi</h2>
            <p className="text-sm text-slate-500 mb-7">Entra con le tue credenziali personali.</p>

            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Email</label>
            <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                   className="w-full mb-4 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-[#FC82ED] focus:ring-2 focus:ring-[#FC82ED]/20 outline-none text-sm text-[#0F0F12] placeholder:text-slate-400 transition-all" placeholder="nome@immobilart.info" />

            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Password</label>
            <div className="relative mb-5">
              <input data-testid="login-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                     className="w-full px-3.5 py-2.5 pr-11 rounded-lg bg-white border border-slate-200 focus:border-[#FC82ED] focus:ring-2 focus:ring-[#FC82ED]/20 outline-none text-sm text-[#0F0F12] placeholder:text-slate-400 transition-all" placeholder="••••••••" />
              <button type="button" data-testid="toggle-password" onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? "Nascondi password" : "Mostra password"} aria-pressed={showPw} title={showPw ? "Nascondi password" : "Mostra password"}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-[#FC82ED] transition-colors focus:outline-none">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {err && <div data-testid="login-error" className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

            <button data-testid="login-submit" type="submit" disabled={loading}
                    className="w-full py-3 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 size={16} className="animate-spin" />} Accedi
            </button>
            <div className="mt-4 text-center">
              <Link to="/forgot-password" data-testid="forgot-link" className="text-sm text-[#FC82ED] hover:underline font-medium">Password dimenticata?</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={submitCode} className="w-full max-w-[340px]" data-testid="login-mfa-step">
            <div className="lg:hidden mb-8"><Logo width={200} /></div>
            <div className="w-11 h-11 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] mb-4">
              <ShieldCheck size={22} />
            </div>
            <h2 className="font-display text-[1.7rem] font-bold tracking-tight mb-1.5">Verifica in due passaggi</h2>
            <p className="text-sm text-slate-500 mb-7">Inserisci il codice a 6 cifre dalla tua app Authenticator, oppure un codice di recupero.</p>

            <input data-testid="login-mfa-code" value={code} onChange={(e) => setCode(e.target.value)} autoFocus inputMode="text" maxLength={14} placeholder="Codice 2FA"
                   className="w-full mb-4 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-[#FC82ED] focus:ring-2 focus:ring-[#FC82ED]/20 outline-none text-sm tracking-[0.25em] text-center transition-all" />

            <label className="flex items-center gap-2.5 mb-5 cursor-pointer select-none">
              <input data-testid="login-remember-device" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                     className="w-4 h-4 accent-[#FC82ED]" />
              <span className="text-sm text-slate-600">Ricorda questo dispositivo per 30 giorni</span>
            </label>

            {err && <div data-testid="login-error" className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

            <button data-testid="login-mfa-submit" type="submit" disabled={loading}
                    className="w-full py-3 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 size={16} className="animate-spin" />} Verifica e accedi
            </button>
            <button type="button" onClick={() => { setMfaToken(null); setCode(""); setErr(""); }} className="w-full mt-3 text-xs text-slate-400 hover:text-slate-600">Torna indietro</button>
          </form>
        )}
      </div>
    </div>
  );
}
